import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  forwardRef,
  inject,
  Input,
  OnDestroy,
} from '@angular/core';
import { ProfileSrvAbstract } from '@base/abstracts/services/profile.abstract';
import { ChangeLogCompany, ProfileCompanyDetail } from '@base/models/responses/profile-me.res';
import { DynamicImportLoaderService } from '@base/services/private/dynamic-import.service';
import { ModalService } from '@base/services/private/modal.service';
import { ModalHeaderComponent } from '@base/components/private/modals/modal-header/modal-header.component';
import { ModalBodyComponent } from '@base/components/private/modals/modal-body/modal-body.component';
import { UpdateCompanyImageRequest, UploadType } from '@base/models/requests/profle-me.req';
import { WhiteLabel } from '@config/whitelabel-keys.constant';
import { Subscription, take } from 'rxjs';
import { GlobalService } from '@base/services/private/global.service';
import { ThemeService } from '@base/services/private/theme.service';
import { TranslatePipe } from '@base/pipes/private/translate.pipe';
import { FileSize } from '@base/constants/file-size';
import { ModalFooterComponent } from '@base/components/private/modals/modal-footer/modal-footer.component';

@Component({
  selector: 'app-modal-edit-company-image',
  imports: [ModalHeaderComponent, ModalBodyComponent, ModalFooterComponent, forwardRef(() => TranslatePipe)],
  templateUrl: './modal-edit-company-image.component.html',
  styleUrl: './modal-edit-company-image.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModalEditCompanyImageComponent implements AfterViewInit, OnDestroy {
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly dynamicImportLoaderSrv = inject(DynamicImportLoaderService);
  private readonly modalSrv = inject(ModalService);
  private readonly globalSrv = inject(GlobalService);
  private readonly themeSrv = inject(ThemeService);

  private profileSrv!: ProfileSrvAbstract;
  @Input() modalId = '';
  @Input() companyData: ProfileCompanyDetail | null = null;
  @Input() uploadType: UploadType = UploadType.COMPANY_LOGO;
  UploadType = UploadType;
  selectedData = {
    file: null as File | null,
    url: '' as string,
  };

  changeLogCompany: ChangeLogCompany | null = null;
  errorMessage = '';
  isHttpRequestRunning = false;
  isBgDarkMode = this.themeSrv.isBgDarkMode;
  httpRequestObserver$: Subscription | null = null;
  /**
   * Closes the modal with the given modalId.
   * @param isUpdated - Indicates if the company data was updated
   * @returns void
   */
  close(isUpdated = false): void {
    this.modalSrv.close(this.modalId, { key: 'MODAL_EDIT_IMAGE', data: isUpdated });
  }

  /**
   * Handles the form submission for uploading or editing a company image.
   *
   * - Constructs the payload from the selected file and upload type.
   * - Calls the `updateCompanyImage` method from the profile service.
   * - On success, closes the modal.
   * - On error, logs the error to the console.
   *
   * @returns void
   */
  onSubmit() {
    if (this.selectedData.file) {
      const data: UpdateCompanyImageRequest = {
        file: this.selectedData.file,
        upload_type: this.uploadType,
      };

      this.profileSrv
        .updateCompanyImage(this.companyData!.id_object!, data)
        .pipe(take(1))
        .subscribe({
          next: () => {
            this.close(true);
          },
          error: (error) => {
            console.error('Error updating company image:', error);
          },
        });
    }
  }

  /**
   * Lifecycle hook called after the component's view has been fully initialized.
   *
   * Loads necessary services asynchronously.
   *
   */
  ngAfterViewInit() {
    (async () => {
      await this.loadServices();
    })();
    this.listenHttpRequestState();
  }

  ngOnDestroy(): void {
    this.httpRequestObserver$?.unsubscribe();
  }

  /**
   * Listens for HTTP request state changes and updates the component state accordingly.
   * @returns void
   */
  listenHttpRequestState(): void {
    this.httpRequestObserver$ = this.globalSrv.httpRequestObserver().subscribe({
      next: (res) => {
        this.isHttpRequestRunning = res;
        this.markToDetectChanges();
      },
    });
  }

  /**
   * Loads necessary services for the app.
   *
   * @returns Promise<void> Resolves when all services are loaded.
   */
  private async loadServices(): Promise<void> {
    try {
      const services = await Promise.all([this.dynamicImportLoaderSrv.loadService(WhiteLabel.SERVICES.Profile)]);

      if (services) {
        const [profileSrv] = services;
        this.profileSrv = profileSrv as ProfileSrvAbstract;
        if (this.profileSrv) {
          this.getChangeLog();
        }
      } else {
        throw new Error('Services could not be loaded.');
      }
    } catch (error) {
      console.error('Failed to load services:', error);
    }
  }

  /**
   * Retrieves last updated information of the company image.
   *
   * - Calls the profile service to get the change log.
   * - Updates the local state and triggers change detection.
   *
   * @returns void
   */
  getChangeLog(): void {
    this.profileSrv
      .companyChangeLog(this.companyData!.id_object!)
      .pipe(take(1))
      .subscribe({
        next: (res: ChangeLogCompany) => {
          this.changeLogCompany = res;
          switch (this.uploadType) {
            case UploadType.COMPANY_LOGO:
              this.selectedData.url = this.changeLogCompany.company_logo;
              break;
            case UploadType.COMPANY_BANNER:
              this.selectedData.url = this.changeLogCompany.company_banner;
              break;
          }
          this.markToDetectChanges();
        },
      });
  }

  /**
   * Handles file input changes for company image upload.
   *
   * - Accepts only image files.
   * - Validates file size (max 1MB).
   * - Validates image dimensions based on upload type (logo or banner).
   * - Sets validation errors if file is invalid.
   * - Updates the preview and triggers change detection.
   * - Automatically submits the image if valid.
   *
   * @param event - The file input change event
   * @returns void
   */
  async onFileChange(event: Event) {
    this.selectedData = {
      file: null,
      url: '',
    };
    const input = event.target as HTMLInputElement;
    const selectedFile = input.files && input.files.length ? input.files[0] : null;
    if (selectedFile && selectedFile.type.startsWith('image/')) {
      if (selectedFile.size > FileSize['1MB']) {
        this.errorMessage = 'FILE_SIZE_LESS_THAN_1MB';
        input.value = '';
        return;
      }

      // Load Image to preview
      const reader = new FileReader();
      reader.onload = (e) => {
        this.selectedData = {
          url: e.target!.result as string,
          file: selectedFile,
        };
        this.markToDetectChanges();
        this.onSubmit();
      };
      reader.readAsDataURL(selectedFile);
    } else {
      this.selectedData = {
        file: null,
        url: '',
      };
    }
  }

  /**
   * Removes the currently selected image.
   *
   * - If the image was uploaded locally, clears the selection.
   * - If the image is from the server, deletes the asset and closes the modal.
   *
   * @returns void
   */
  removeImage(): void {
    this.selectedData.url = '';
    const localUploaded = this.selectedData.file ? true : false;
    this.markToDetectChanges();
    if (localUploaded) {
      // Only uploaded from local, no need to close modal
      this.selectedData.file = null;
    } else {
      // Proceed delete image and close modal
      this.deleteImage();
    }
  }

  /**
   * Deletes the company image asset from the server.
   *
   * - Calls the profile service to delete the asset.
   * - On success, closes the modal and triggers change detection.
   * - On error, logs the error to the console.
   *
   * @returns void
   */
  deleteImage() {
    this.profileSrv
      .deleteCompanyAssets(this.companyData!.id_object!, this.uploadType)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.close(true);
          this.markToDetectChanges();
        },
        error: (error) => {
          console.error('Error deleting company asset:', error);
        },
      });
  }

  /**
   * Triggers Angular change detection and marks the component for check.
   *
   * Ensures that updates to the component's data or state are reflected in the view,
   * especially when using OnPush change detection strategy.
   *
   * @returns void
   */
  private markToDetectChanges(): void {
    this.changeDetectorRef.detectChanges();
    this.changeDetectorRef.markForCheck();
  }

  /**
   * Returns a formatted string with the last editor's name and timestamp for the company image.
   *
   * - For logo uploads, includes the editor's name (if available) and the last edit timestamp.
   * - For banner uploads, includes the editor's name (if available) and the last edit timestamp.
   * - If no information is available, returns null.
   *
   * @returns string The formatted last edit information or null if there is no last edit information.
   */
  get lastEditInfo(): string | null {
    switch (this.uploadType) {
      case UploadType.COMPANY_LOGO: {
        const name = this.changeLogCompany?.changelogs?.company_logo?.name;
        const timestamp = this.changeLogCompany?.changelogs?.company_logo?.timestamp;
        return (name ? `${name}, ` : '') + (timestamp ?? '');
      }
      case UploadType.COMPANY_BANNER: {
        const name = this.changeLogCompany?.changelogs?.company_banner?.name;
        const timestamp = this.changeLogCompany?.changelogs?.company_banner?.timestamp;
        return (name ? `${name}, ` : '') + (timestamp ?? '');
      }
      default:
        return null;
    }
  }
}
